import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Database, Tables, TablesInsert, TablesUpdate, Enums } from '../types/supabase';

// Re-export types from supabase.ts
export type { Tables, TablesInsert, TablesUpdate, Enums };

// Create convenience type aliases for each table
export type User = Tables<'users'>;
export type UserInsert = TablesInsert<'users'>;
export type UserUpdate = TablesUpdate<'users'>;

export type Submission = Tables<'submissions'>;
export type SubmissionInsert = TablesInsert<'submissions'>;
export type SubmissionUpdate = TablesUpdate<'submissions'>;

export type Document = Tables<'documents'>;
export type DocumentInsert = TablesInsert<'documents'>;
export type DocumentUpdate = TablesUpdate<'documents'>;

export type ReviewDecision = Tables<'review_decisions'>;
export type ReviewDecisionInsert = TablesInsert<'review_decisions'>;
export type ReviewDecisionUpdate = TablesUpdate<'review_decisions'>;

export type AuditLog = Tables<'audit_logs'>;
export type AuditLogInsert = TablesInsert<'audit_logs'>;
export type AuditLogUpdate = TablesUpdate<'audit_logs'>;

// Create type aliases for enums
export type DecisionType = Enums<'decision_type'>;
export type SubmissionClassification = Enums<'submission_classification'>;
export type SubmissionStatus = Enums<'submission_status'>;
export type SubmissionType = Enums<'submission_type'>;
export type UserRole = Enums<'user_role'>;

// Create a more specific type for our Supabase client
export type TypedSupabaseClient = SupabaseClient<Database>;

// Environment variables validation
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;


if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// To prevent multiple instance warnings, we check if we already have a global instance
// This helps with hot module replacement during development
declare global {
  interface Window {
    // Use a unique key that won't conflict with other variables
    __TEMPUS_SUPABASE_CLIENT?: TypedSupabaseClient;
  }
}

/**
 * Get the Supabase client instance
 * @returns SupabaseClient instance
 */
export function getSupabaseClient(): TypedSupabaseClient {
  if (typeof window !== 'undefined') {
    // Browser environment - use global window object to store singleton
    if (!window.__TEMPUS_SUPABASE_CLIENT) {
      window.__TEMPUS_SUPABASE_CLIENT = createClient<Database>(supabaseUrl!, supabaseAnonKey!, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
        }
      });
    }
    return window.__TEMPUS_SUPABASE_CLIENT;
  } else {
    // Server environment (Node.js) or during SSR
    // Use module-level singleton
    if (!supabaseInstance) {
      supabaseInstance = createClient<Database>(supabaseUrl!, supabaseAnonKey!);
    }
    return supabaseInstance;
  }
}

// Module-level singleton for server-side or fallback
let supabaseInstance: TypedSupabaseClient | null = null;